import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useSWR from "swr";
import { useSnackbar } from "notistack";
import { TableCell, TableRow, TableBody } from "@material-ui/core";
import { getUser } from "utils/authentication";
import Items from "./Items";
import ListSkeleton from "./ListSkeleton";
import TitleAndTable from "./TitleAndTable";
import * as api from "../utils";

import { UpdateUserBody, UserResourceList } from "@dashboard-v3/api";

export default function OrganizationUsers() {
  const { t } = useTranslation("users");
  const { enqueueSnackbar } = useSnackbar();
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const swr = useSWR("/users", fetchUsers);

  function withPending<T>(id: string, callback: () => Promise<T>) {
    setPendingIds(previous => new Set(previous).add(id));
    return callback().finally(() => {
      setPendingIds(previous => {
        const next = new Set(previous);
        next.delete(id);
        return next;
      });
    });
  }

  async function deleteUser(userId: string) {
    try {
      await withPending(userId, async () => {
        await api.deleteUser(userId);
        await swr.mutate();
      });
    } catch (error) {
      api.handleError("deleteUser", error, errors =>
        enqueueSnackbar(t(errors))
      );
    }
  }

  async function updateUser(id: string, changes: UpdateUserBody) {
    try {
      await withPending(id, async () => {
        await api.updateUser(id, changes);
        await swr.mutate();
      });
    } catch (error) {
      api.handleError("patchUser", error, errors => enqueueSnackbar(t(errors)));
    }
  }

  if (swr.isLoading) {
    return (
      <TitleAndTable testId="user-list_loading">
        <ListSkeleton />
      </TitleAndTable>
    );
  }

  if (swr.error) {
    return (
      <TitleAndTable testId="user-list_error">
        <TableBody>
          <TableRow>
            <TableCell colSpan={6} style={{ padding: "15px 16px" }}>
              {t("error.fetchUserList.SYSTEM_FAILURE")}
            </TableCell>
          </TableRow>
        </TableBody>
      </TitleAndTable>
    );
  }

  return (
    <TitleAndTable testId="user-list">
      <StyledTableBody>
        <Items
          users={swr.data}
          pendingIds={pendingIds}
          onDelete={deleteUser}
          onChange={updateUser}
        />
      </StyledTableBody>
    </TitleAndTable>
  );
}

async function fetchUsers(): Promise<UserResourceList> {
  const currentUser = getUser();
  const res = await api.fetchUserList();
  return res
    .sort((u1, u2) => u2.updatedAt - u1.updatedAt)
    .filter(u => currentUser.id !== u.id);
}

const StyledTableBody = styled(TableBody)`
  tr {
    background-color: white;
    :hover {
      background-color: rgba(0, 0, 0, 0.04);
    }
  }

  tr:first-child td:first-child {
    border-radius: 4px 0 0 0;
  }

  tr:first-child td:last-child {
    border-radius: 0 4px 0 0;
  }

  tr:last-child td:first-child {
    border: 0;
    border-radius: 0 0 0 4px;
  }

  tr:last-child td:last-child {
    border: 0;
    border-radius: 0 0 4px 0;
  }
`;
